import { useState } from "react";
import "./PrivacyPolicy.css";

const PrivacyPolicy = () => {
  const [expanded, setExpanded] = useState<string | null>("panel1");

  const togglePanel = (panel: string) => {
    setExpanded(expanded === panel ? null : panel);
  };

  return (
    <div className="legal-page">
      <div className="legal-header">
        <div className="legal-container">
          <div className="breadcrumb">
            <span className="icon">🏠</span>
            <a href="/">Home</a>
            <span className="icon">›</span>
            <span>Privacy Policy</span>
          </div>

          <h1 className="legal-title">Privacy Policy</h1>
          <p className="legal-subtitle">Last Updated: November 21, 2025</p>
        </div>
      </div>

      <div className="legal-container">
        <div className="intro-box">
          <p>
            At Learn and Innovate Technologies Limited (LICO), we are committed to
            protecting your privacy and ensuring the security of your personal
            information. This Privacy Policy explains how we collect, use,
            store, and protect your data when you use our services.
          </p>
        </div>

        <div className="accordion-group">
          <div
            className={`accordion-item ${
              expanded === "panel1" ? "active" : ""
            }`}
          >
            <button
              className="accordion-header"
              onClick={() => togglePanel("panel1")}
            >
              <span>1. Information We Collect</span>
              <span className="icon">{expanded === "panel1" ? "−" : "+"}</span>
            </button>
            <div className="accordion-content">
              <h4>1.1 Personal Information</h4>
              <p>
                We collect personal information that you provide directly to us,
                including: name, email address, phone number, physical address,
                date of birth, educational background, professional experience,
                payment information, and identification documents.
              </p>

              <h4>1.2 Service-Specific Information</h4>
              <p>
                <strong>Training Services:</strong> Academic records, course
                preferences, attendance records, and assessment results.
              </p>
              <p>
                <strong>Solar Solutions:</strong> Property details, energy
                consumption patterns, site photos, and installation preferences.
              </p>
              <p>
                <strong>Software Development:</strong> Business requirements,
                technical specifications, and project communications.
              </p>
              <p>
                <strong>Real Estate:</strong> Financial information, property
                preferences, investment capacity, and transaction history.
              </p>

              <h4>1.3 Automatically Collected Information</h4>
              <p>
                We automatically collect certain information when you visit our
                website, including IP address, browser type, device information,
                pages visited, time spent on pages, and referring website
                addresses.
              </p>
            </div>
          </div>

          <div
            className={`accordion-item ${
              expanded === "panel2" ? "active" : ""
            }`}
          >
            <button
              className="accordion-header"
              onClick={() => togglePanel("panel2")}
            >
              <span>2. How We Use Your Information</span>
              <span className="icon">{expanded === "panel2" ? "−" : "+"}</span>
            </button>
            <div className="accordion-content">
              <p>We use your information to:</p>
              <ul>
                <li>Provide and deliver our services</li>
                <li>Process payments and transactions</li>
                <li>Send service-related communications and updates</li>
                <li>Respond to inquiries and provide customer support</li>
                <li>Improve our services and develop new offerings</li>
                <li>Conduct market research and analysis</li>
                <li>Comply with legal obligations and regulations</li>
                <li>Prevent fraud and enhance security</li>
                <li>Send marketing communications (with your consent)</li>
                <li>Personalize your experience with our services</li>
              </ul>
            </div>
          </div>

          <div
            className={`accordion-item ${
              expanded === "panel3" ? "active" : ""
            }`}
          >
            <button
              className="accordion-header"
              onClick={() => togglePanel("panel3")}
            >
              <span>3. Information Sharing and Disclosure</span>
              <span className="icon">{expanded === "panel3" ? "−" : "+"}</span>
            </button>
            <div className="accordion-content">
              <p>We may share your information with:</p>
              <ul>
                <li>
                  <strong>Service Providers:</strong> Third-party vendors who
                  assist in service delivery (payment processors, hosting
                  providers, software tools)
                </li>
                <li>
                  <strong>Business Partners:</strong> Collaborators in joint
                  service offerings or referral arrangements
                </li>
                <li>
                  <strong>Legal Authorities:</strong> When required by law or to
                  protect our rights and safety
                </li>
                <li>
                  <strong>Professional Advisors:</strong> Lawyers, accountants,
                  and consultants bound by confidentiality
                </li>
                <li>
                  <strong>Business Transfers:</strong> In connection with
                  mergers, acquisitions, or asset sales
                </li>
              </ul>
              <p>
                We do not sell your personal information to third parties for
                marketing purposes.
              </p>
            </div>
          </div>

          <div
            className={`accordion-item ${
              expanded === "panel4" ? "active" : ""
            }`}
          >
            <button
              className="accordion-header"
              onClick={() => togglePanel("panel4")}
            >
              <span>4. Data Security</span>
              <span className="icon">{expanded === "panel4" ? "−" : "+"}</span>
            </button>
            <div className="accordion-content">
              <p>
                We implement appropriate technical and organizational measures
                to protect your personal information, including encryption,
                secure servers, access controls, regular security assessments,
                employee training on data protection, and incident response
                procedures.
              </p>
              <p>
                However, no method of transmission over the internet or
                electronic storage is completely secure. While we strive to
                protect your information, we cannot guarantee absolute security.
              </p>
            </div>
          </div>

          <div
            className={`accordion-item ${
              expanded === "panel5" ? "active" : ""
            }`}
          >
            <button
              className="accordion-header"
              onClick={() => togglePanel("panel5")}
            >
              <span>5. Your Rights and Choices</span>
              <span className="icon">{expanded === "panel5" ? "−" : "+"}</span>
            </button>
            <div className="accordion-content">
              <p>You have the right to:</p>
              <ul>
                <li>Access your personal information</li>
                <li>Correct inaccurate or incomplete information</li>
                <li>
                  Request deletion of your information (subject to legal
                  requirements)
                </li>
                <li>Opt-out of marketing communications</li>
                <li>Restrict processing of your information</li>
                <li>
                  Data portability (receive your data in a structured format)
                </li>
                <li>Object to processing based on legitimate interests</li>
                <li>Withdraw consent where processing is based on consent</li>
              </ul>
              <p>
                To exercise these rights, contact us at privacy@licotech.com
              </p>
            </div>
          </div>

          <div
            className={`accordion-item ${
              expanded === "panel6" ? "active" : ""
            }`}
          >
            <button
              className="accordion-header"
              onClick={() => togglePanel("panel6")}
            >
              <span>6. Cookies and Tracking Technologies</span>
              <span className="icon">{expanded === "panel6" ? "−" : "+"}</span>
            </button>
            <div className="accordion-content">
              <p>
                We use cookies and similar tracking technologies to enhance your
                experience, analyze website traffic, and remember your
                preferences. You can control cookies through your browser
                settings, but disabling cookies may affect website
                functionality.
              </p>
              <p>Types of cookies we use:</p>
              <ul>
                <li>Essential cookies (required for website operation)</li>
                <li>Performance cookies (website analytics)</li>
                <li>Functional cookies (remember preferences)</li>
                <li>Marketing cookies (personalized advertising)</li>
              </ul>
            </div>
          </div>

          <div
            className={`accordion-item ${
              expanded === "panel7" ? "active" : ""
            }`}
          >
            <button
              className="accordion-header"
              onClick={() => togglePanel("panel7")}
            >
              <span>7. Data Retention</span>
              <span className="icon">{expanded === "panel7" ? "−" : "+"}</span>
            </button>
            <div className="accordion-content">
              <p>
                We retain your personal information for as long as necessary to
                fulfill the purposes outlined in this policy, comply with legal
                obligations, resolve disputes, and enforce our agreements.
                Specific retention periods vary by information type and service
                category.
              </p>
            </div>
          </div>

          <div
            className={`accordion-item ${
              expanded === "panel8" ? "active" : ""
            }`}
          >
            <button
              className="accordion-header"
              onClick={() => togglePanel("panel8")}
            >
              <span>8. Children's Privacy</span>
              <span className="icon">{expanded === "panel8" ? "−" : "+"}</span>
            </button>
            <div className="accordion-content">
              <p>
                Our services are not directed to individuals under 18 years of
                age. We do not knowingly collect personal information from
                children. If you believe we have collected information from a
                child, please contact us immediately.
              </p>
            </div>
          </div>

          <div
            className={`accordion-item ${
              expanded === "panel9" ? "active" : ""
            }`}
          >
            <button
              className="accordion-header"
              onClick={() => togglePanel("panel9")}
            >
              <span>9. International Data Transfers</span>
              <span className="icon">{expanded === "panel9" ? "−" : "+"}</span>
            </button>
            <div className="accordion-content">
              <p>
                Your information may be transferred to and processed in
                countries other than Nigeria. We ensure appropriate safeguards
                are in place to protect your information in accordance with this
                Privacy Policy and applicable data protection laws.
              </p>
            </div>
          </div>

          <div
            className={`accordion-item ${
              expanded === "panel10" ? "active" : ""
            }`}
          >
            <button
              className="accordion-header"
              onClick={() => togglePanel("panel10")}
            >
              <span>10. Changes to This Policy</span>
              <span className="icon">{expanded === "panel10" ? "−" : "+"}</span>
            </button>
            <div className="accordion-content">
              <p>
                We may update this Privacy Policy periodically. We will notify
                you of significant changes by posting the new policy on our
                website and updating the "Last Updated" date. Your continued use
                of our services after changes constitutes acceptance of the
                updated policy.
              </p>
            </div>
          </div>

          <div
            className={`accordion-item ${
              expanded === "panel11" ? "active" : ""
            }`}
          >
            <button
              className="accordion-header"
              onClick={() => togglePanel("panel11")}
            >
              <span>11. Contact Us</span>
              <span className="icon">{expanded === "panel11" ? "−" : "+"}</span>
            </button>
            <div className="accordion-content">
              <p>For privacy-related questions or concerns:</p>
              <div className="contact-info">
                <div className="contact-item">
                  <span className="icon">✉️</span>
                  <span>privacy@licotech.com</span>
                </div>
                <div className="contact-item">
                  <span className="icon">📞</span>
                  <span>+234 907 880 1125</span>
                </div>
                <div className="contact-item">
                  <span className="icon">📍</span>
                  <span>Port Harcourt, Rivers State, Nigeria</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="acknowledgment-box">
          <h3>Your Consent</h3>
          <p>
            By using LICO's services, you consent to the collection and use of
            your information as described in this Privacy Policy. We value your
            trust and are committed to protecting your privacy.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
